import Delete from "@mui/icons-material/Delete";
import AudiotrackOutlined from "@mui/icons-material/AudiotrackOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import PlayArrowOutlined from "@mui/icons-material/PlayArrowOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import DOMPurify from "dompurify";
import { User } from "../../utils";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import { AUDIO_SPEED_OPTIONS, DEFAULT_AUDIO_VOICE, DEFAULT_AUDIO_SPEED, DEFAULT_AUDIO_MODEL } from "../../constants";
import { formatTimestamp, hostname, parseError } from "../../utils";

export default function Audio({
  currentUser,
  getToken,
  eventListener,
  companyId,
}: {
  getToken: Function;
  eventListener: Function;
  currentUser: any | User;
  companyId: string;
}) {
  const [init, setInit] = useState(false);
  const [audioFiles, setAudioFiles] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [audioDialogOpen, setAudioDialogOpen] = useState(false);
  const [audioDialogLoading, setAudioDialogLoading] = useState(false);
  const [audioText, setAudioText] = useState("");
  const [voiceId, setVoiceId] = useState("");
  const [speed, setSpeed] = useState(DEFAULT_AUDIO_SPEED);
  const [model, setModel] = useState("eleven_flash_v2_5");
  const [audioPreviewOpen, setAudioPreviewOpen] = useState(false);
  const [selectedAudio, setSelectedAudio] = useState<any>({});
  const [voices, setVoices] = useState<Array<any>>([]);
  const [models, setModels] = useState<Array<any>>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAudioForMenu, setSelectedAudioForMenu] = useState<any>(null);

  const getVoiceName = (voiceId: string) => {
    const voice = voices.find(v => v.voiceId === voiceId);
    return voice ? voice.name : voiceId;
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const deleteAudio = async (e: any, audioId: string) => {
    const currentToken = await getToken();
    setLoading(true);
    axios
      .delete(`${hostname}/audio/${audioId}`, {
        params: {
          companyId,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        getData();
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const previewAudio = (e: any, audio: any) => {
    setSelectedAudio(audio);
    setAudioPreviewOpen(true);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, audio: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedAudioForMenu(audio);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAudioForMenu(null);
  };

  const handlePreviewFromMenu = () => {
    if (selectedAudioForMenu) {
      previewAudio(null, selectedAudioForMenu);
    }
    handleMenuClose();
  };

  const handleDeleteFromMenu = () => {
    if (selectedAudioForMenu) {
      deleteAudio(null, selectedAudioForMenu.id);
    }
    handleMenuClose();
  };

  const handleDownloadFromMenu = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    // Store reference before closing menu
    const audioToDownload = selectedAudioForMenu;
    handleMenuClose();

    if (audioToDownload && audioToDownload.publicUrl) {
      try {
        const response = await fetch(audioToDownload.publicUrl);
        if (!response.ok) {
          throw new Error("Failed to download audio");
        }
        const blob = await response.blob();
        const downloadBlob = new Blob([blob], { type: 'application/octet-stream' });
        const downloadUrl = window.URL.createObjectURL(downloadBlob);

        const link = document.createElement('a');
        link.style.display = 'none';
        link.href = downloadUrl;
        link.download = `audio_${audioToDownload.id}.mp3`;
        link.click();

        // Clean up
        setTimeout(() => {
          window.URL.revokeObjectURL(downloadUrl);
        }, 100);

        eventListener({
          type: "SET_SUCCESS",
          message: "Audio downloaded successfully",
        });
      } catch (error) {
        console.error("Error downloading audio:", error);
        eventListener({
          type: "SET_ERROR",
          error: "Failed to download audio",
        });
      }
    }
  };

  const createAudio = async () => {
    const currentToken = await getToken();
    setAudioDialogLoading(true);
    const req: any = {
      text: audioText,
      voiceId: voiceId,
      speed: speed,
      model: model,
      companyId: companyId,
    };

    axios
      .post(`${hostname}/audio`, req, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then(() => {
        getData();
        setAudioText("");
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setAudioDialogLoading(false);
        setAudioDialogOpen(false);
      });
  };

  const getVoices = async () => {
    const currentToken = await getToken();
    try {
      const response = await axios.get(`${hostname}/audio/voices`, {
        params: {
          companyId,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      setVoices(response.data.items || []);
      // Set first voice as default if none selected
      if (!voiceId && response.data.items && response.data.items.length > 0) {
        setVoiceId(response.data.items[0].voiceId);
      }
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  };

  const getModels = async () => {
    const currentToken = await getToken();
    try {
      const response = await axios.get(`${hostname}/audio/models`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      setModels(response.data || []);
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    }
  };

  const getData = async () => {
    const currentToken = await getToken();
    setLoading(true);
    axios
      .get(`${hostname}/audio`, {
        params: {
          companyId,
          page: page + 1,
          pageSize,
        },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setAudioFiles(res.data.items);
        setTotal(res.data.total);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (currentUser.uid) {
      getData();
      getVoices();
      getModels();
    }
  }, [currentUser, companyId, page]);

  if (loading && !init) {
    return <LinearProgress sx={{ mt: 2 }} />;
  }

  return (
    <Box>
      <div style={{ width: "100%", height: 40 }}>
        <Button
          data-tutorial="create-audio"
          variant="contained"
          style={{ marginTop: 10 }}
          startIcon={<AudiotrackOutlined />}
          onClick={() => setAudioDialogOpen(true)}
        >
          Create Audio
        </Button>
        <IconButton
          onClick={() => getData()}
          style={{ float: "right", marginTop: 10 }}
        >
          <RefreshOutlined />
        </IconButton>
      </div>
      {audioFiles.length == 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography>No audio files found.</Typography>
        </Box>
      )}
      <TableContainer data-tutorial="audio-list">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Preview</TableCell>
              <TableCell>Text</TableCell>
              <TableCell>Voice</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {audioFiles.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  {item.publicUrl ? (
                    <Box
                      sx={{
                        width: 80,
                        height: 40,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        position: "relative",
                      }}
                      onClick={(e) => previewAudio(e, item)}
                    >
                      <AudiotrackOutlined sx={{ fontSize: 16, color: "primary.main" }} />
                      <PlayArrowOutlined
                        sx={{
                          position: "absolute",
                          fontSize: 12,
                          color: "white",
                          backgroundColor: "rgba(0,0,0,0.6)",
                          borderRadius: "50%",
                          p: 0.25,
                          bottom: 2,
                          right: 2,
                        }}
                      />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: 80,
                        height: 40,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <AudiotrackOutlined sx={{ fontSize: 16, color: "grey.500" }} />
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                    {item.text}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={getVoiceName(item.voice) || item.voice || "Unknown"} size="small" />
                </TableCell>
                <TableCell>
                  <Chip label="ElevenLabs" size="small" color="primary" />
                </TableCell>
                <TableCell>
                  {formatTimestamp(item.created).fullDateTime}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    onClick={(e) => handleMenuClick(e, item)}
                    size="small"
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Dialog open={audioDialogOpen} onClose={() => setAudioDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Create Audio
          <IconButton
            aria-label="close"
            onClick={() => setAudioDialogOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Convert text to speech using ElevenLabs voices
          </DialogContentText>

          <FormControl fullWidth style={{ marginTop: 20 }}>
            <InputLabel>Model</InputLabel>
            <Select
              label="Model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
            >
              {models.map((modelOption) => (
                <MenuItem value={modelOption.id} key={modelOption.id}>
                  {modelOption.name}
                  <Chip
                    label={`${modelOption.character_limit.toLocaleString()} chars`}
                    size="small"
                    style={{ marginLeft: 8 }}
                    variant="outlined"
                  />
                  <Chip
                    label={`~${modelOption.duration_minutes} min`}
                    size="small"
                    style={{ marginLeft: 4 }}
                    variant="outlined"
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            style={{ marginTop: 20 }}
            label="Text"
            value={audioText}
            fullWidth
            multiline
            rows={4}
            onChange={(e: any) => setAudioText(e.target.value)}
            helperText={`Enter the text you want converted to speech (${audioText.length} / ${models.find(m => m.id === model)?.character_limit?.toLocaleString() || 'N/A'} characters)`}
          />

          <FormControl fullWidth style={{ marginTop: 20 }}>
            <InputLabel>Voice</InputLabel>
            <Select
              label="Voice"
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
            >
              {voices.length === 0 && (
                <MenuItem value="" disabled>
                  No voices available
                </MenuItem>
              )}
              {voices.map((voice) => (
                <MenuItem value={voice.voiceId} key={voice.id}>
                  {voice.name}
                  {voice.companyId && (
                    <Chip
                      label="Company"
                      size="small"
                      style={{ marginLeft: 8 }}
                      color="primary"
                      variant="outlined"
                    />
                  )}
                  {!voice.companyId && (
                    <Chip
                      label="Public"
                      size="small"
                      style={{ marginLeft: 8 }}
                      variant="outlined"
                    />
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth style={{ marginTop: 20 }}>
            <InputLabel>Speed</InputLabel>
            <Select
              label="Speed"
              value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))}
            >
              {[0.7, 0.8, 0.9, 1.0, 1.1, 1.2].map((value) => (
                <MenuItem key={value} value={value}>
                  {value === 1.0 ? '1.0x (Normal)' : `${value}x`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Chip
            label="5 Credits"
            style={{ marginRight: 'auto' }}
          />
          <Button onClick={() => setAudioDialogOpen(false)}>
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={createAudio}
            loading={audioDialogLoading}
            disabled={!audioText || !voiceId}
          >
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        maxWidth="md"
        fullWidth
        open={audioPreviewOpen}
        onClose={() => setAudioPreviewOpen(false)}
      >
        <DialogTitle>Audio Preview</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Text: {selectedAudio.text}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Voice: {getVoiceName(selectedAudio.voice) || selectedAudio.voice}
          </Typography>
          {selectedAudio.publicUrl && (
            <audio controls style={{ width: "100%" }}>
              <source src={selectedAudio.publicUrl} type="audio/mpeg" />
              Your browser does not support the audio element.
            </audio>
          )}
        </DialogContent>
      </Dialog>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handlePreviewFromMenu}>
          <ListItemIcon>
            <PlayArrowOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Preview Audio</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownloadFromMenu}>
          <ListItemIcon>
            <DownloadOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteFromMenu}>
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
